/**
 * PronunciationBench — the LitGroup's respellings, and the audition that decides them.
 *
 * ⭐ THIS IS NOT A FORM WITH A PREVIEW BUTTON, and the difference is the whole feature. Two
 * measurements shaped it:
 *
 *   1. ⛔ A CANDIDATE CAN ONLY BE JUDGED IN CONTEXT. "WAXWANDS" read correctly 4 times in 4 in an
 *      isolated sentence and WRONGLY 3 times in 3 in its real 1,382-character chunk. So the preview
 *      renders the word's OWN passage out of this book — never the bare word.
 *
 *   2. ⛔ ONE TAKE PROVES NOTHING. "wyvern" mispronounces in about 1 generation in 5 (4 of 20,
 *      measured). A single preview of the BROKEN spelling sounds fine four times out of five. So
 *      every audition renders three takes a side, control first, and shows them as three.
 *
 * ⚠️ AND A THIRD THING THIS CANNOT SETTLE, said plainly in the UI so nobody expects it to: takes
 * also vary in EMPHASIS — "female" over-stressed, "Other" heavily stressed — which has nothing to
 * do with spelling and which no entry can fix.
 *
 * ⚠️ SCOPE. The map is per-LITGROUP; this panel sits in a per-BOOK page. Editing it here changes
 * pronunciation for every book in the group. That is intended, and it is exactly the kind of thing
 * people mis-read, so the panel says it out loud.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Checkbox, Confirm, Dropdown, Header, Icon, Input, Label, List, Message, Segment,
} from 'semantic-ui-react';
import { apiClient, TIMEOUTS } from '../../apiTools/apiClient';

const BLANK = { from: '', to: '', mode: 'exact', caseSensitive: false };

const MODE_OPTIONS = [
  { key: 'exact', value: 'exact', text: 'Exact word' },
  { key: 'stem', value: 'stem', text: 'Word + endings' },
];

export default function PronunciationBench({
  litGroupUuid, bookId, bookTitle, captured = null, onClearCapture,
}) {
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState(BLANK);
  const [editingFrom, setEditingFrom] = useState(null);   // the entry being replaced, by `from`
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [takes, setTakes] = useState(null);               // { excerpt, occurrences, takes[] }
  const [auditioning, setAuditioning] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pasted, setPasted] = useState('');                // text dropped or pasted straight in
  const [dropActive, setDropActive] = useState(false);
  const [cleared, setCleared] = useState([]);              // heard, and deliberately left alone
  const [impact, setImpact] = useState(null);              // blast radius of the pending decision

  /**
   * ⭐ WHICH PASSAGE THE AUDITION USES, and why it can be told to use one.
   *
   * Left to itself the server searches the book and picks the LONGEST line carrying the word. That
   * is a reasonable default and it produced a misleading first result: a 701-character passage the
   * operator had never listened to, standing in for the short one he actually heard fail. A
   * respelling can only be judged where it broke, so the passage has to be nameable.
   *
   * Precedence is most-deliberate-first — text typed or dropped here beats a captured line, which
   * beats the book search.
   */
  const passage = pasted.trim()
    ? { text: pasted.trim(), kind: 'pasted' }
    : (captured && captured.text ? { text: captured.text, kind: 'captured' } : null);

  const load = useCallback(async () => {
    if (!litGroupUuid) return;
    try {
      const r = await apiClient.get(`/litgroups/${litGroupUuid}/pronunciation`);
      setEntries(r.data.entries || []);
      setCleared(r.data.cleared || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load the pronunciation map');
    }
  }, [litGroupUuid]);

  useEffect(() => { load(); }, [load]);

  /**
   * ⭐ TWO DIFFERENT ACTS, and the first cut of this panel conflated them.
   *
   *   FIND AND HEAR      one word — how often it occurs, and how the narrator says it today
   *   AUDITION           control against a candidate respelling, three takes a side
   *
   * The button used to be gated on `from && to`, so you could not ask "how does the model say
   * Glendyl?" without first inventing a respelling to compare it against. That is backwards for the
   * question that comes first, and it made the operator's own workflow — typing in words they
   * already suspect — impossible. `canHear` needs only a word.
   */
  const canHear = !!draft.from.trim();
  const dirty = !!(draft.from.trim() && draft.to.trim());
  const isEdit = editingFrom !== null;
  const clearedHit = cleared.find((c) => c.word.toLowerCase() === draft.from.trim().toLowerCase());

  const reset = () => {
    setDraft(BLANK); setEditingFrom(null); setTakes(null); setMsg(null); setImpact(null);
  };

  /**
   * What does this decision cost, measured against what is already rendered?
   *
   * ⚠️ ASKED BEFORE EVERY COMMIT, in both directions. Adding `Glendyl → Glendil` invalidated 330 of
   * 1,483 rendered lines on LN1 — 22% of the book's audio. REMOVING it does exactly the same, since
   * the narrator goes back to saying "Glendyl". Never fails loudly: an unmeasurable impact must not
   * block a decision, it just means the warning cannot be shown.
   */
  const measure = useCallback(async (entry) => {
    if (!bookId || !entry?.from) return null;
    try {
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/books/${bookId}/pronunciation/impact`, entry);
      return r.data?.known ? r.data : null;
    } catch (_) {
      return null;
    }
  }, [litGroupUuid, bookId]);

  const audition = async () => {
    setAuditioning(true); setError(null); setTakes(null);
    try {
      // ⚠️ NOT the default 30s. Six TTS generations back to back take minutes, and an aborted
      // request has no response body — which is why the first failure here read only
      // "Audition failed" and said nothing about why.
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/books/${bookId}/pronunciation/preview`,
        // ⚠️ `excerpt` is a request parameter, NOT part of the entry — the server splits it back
        // off before validating, so a passage can never leak into the saved map.
        passage ? { ...draft, excerpt: passage.text } : draft, {},
        { timeout: TIMEOUTS.AUDITION });
      setTakes(r.data);
      if (!r.data.found) {
        setMsg(`"${draft.from}" does not appear in ${bookTitle || 'this book'} — you can still save it for the group's other books, but you cannot hear it here.`);
      }
    } catch (e) {
      // ⚠️ DISTINGUISH THE THREE, because they need different actions and they arrive looking the
      // same. A bare "Audition failed" is what this said the first time it timed out, and it sent
      // the operator looking for a server fault that had not happened.
      if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')) {
        setError('The audition took too long and was cut off. The takes may still be rendering on the server — try again in a moment.');
      } else if (!e.response) {
        setError(`The request never completed: ${e.message || 'no response from the server'}`);
      } else {
        setError(e.response.data?.error || `Audition failed (HTTP ${e.response.status})`);
      }
    } finally {
      setAuditioning(false);
    }
  };

  /**
   * "Heard it, it is fine, stop asking." The third state, and the one that makes a worklist out of
   * a report — without it, 212 candidate names are re-triaged every session.
   *
   * ⭐ FREE TO UNDO. Clearing a word changes nothing that was ever rendered; it only removes a row
   * from the list of things still to hear. That is why this commits with no confirmation while
   * removing a map ENTRY demands one.
   */
  const saveCleared = async (next) => {
    setBusy(true); setError(null);
    try {
      const r = await apiClient.put(`/litgroups/${litGroupUuid}/pronunciation/cleared`, { cleared: next });
      setCleared(r.data.cleared || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const markFine = async () => {
    const word = draft.from.trim();
    if (!word) return;
    await saveCleared([...cleared.filter((c) => c.word.toLowerCase() !== word.toLowerCase()), { word }]);
    setMsg(`"${word}" recorded as fine as-is. It will not be raised again — you can undo that below.`);
    reset();
  };

  // ⭐ UN-DECLINING IS THE FREE DIRECTION. No audio depends on it, so no warning is owed.
  const unclear = (word) => saveCleared(cleared.filter((c) => c.word !== word));

  const commit = async () => {
    setConfirmSave(false); setBusy(true); setError(null);
    try {
      // ⚠️ The map is replaced WHOLESALE, so the full list is sent every time. Editing swaps the
      // entry in place rather than appending, which is what makes click-to-edit non-destructive.
      const next = isEdit
        ? entries.map((e) => (e.from === editingFrom ? { ...draft } : e))
        : [...entries, { ...draft }];
      const r = await apiClient.put(`/litgroups/${litGroupUuid}/pronunciation`, { entries: next });
      setEntries(r.data.entries || []);
      if (r.data.rejected?.length) {
        setError(r.data.rejected.map((x) => x.reason).join(' '));
      } else {
        setMsg(`Saved. Applies to every book in this Literary Group on the next FORCE re-render.`);
        reset();
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (from) => {
    setConfirmDelete(null); setBusy(true); setError(null);
    try {
      const r = await apiClient.put(`/litgroups/${litGroupUuid}/pronunciation`,
        { entries: entries.filter((e) => e.from !== from) });
      setEntries(r.data.entries || []);
      if (editingFrom === from) reset();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove');
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const t = takes?.takes || [];
    return { control: t.filter((x) => x.kind === 'control'), candidate: t.filter((x) => x.kind === 'candidate') };
  }, [takes]);

  if (!litGroupUuid || !bookId) return null;

  return (
    <Segment className="rs-pronunciation">
      <Header as="h5" style={{ marginBottom: '0.25em' }}>
        <Icon name="microphone slash" /> Pronunciation
      </Header>
      {/* ⚠️ Scope, stated where it cannot be missed. */}
      <div style={{ fontSize: '0.82em', color: '#777', marginBottom: '0.75em' }}>
        Respellings for this <strong>Literary Group</strong> — they change what the narrator SAYS,
        never what the book says, and apply to every book in the group.
      </div>

      {/* ── the entry row ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          /* ⓘ "or phrase" since 2026-10-19: up to four words, so a numbered title can be respelled
             (`Madonna 13. Written` → `Madonna 13, written`). A longer span is an editorial
             correction and belongs to the overlay, which is per-book and keeps a reason. */
          size="small" placeholder="Word or short phrase in the book…" value={draft.from} disabled={busy}
          onChange={(_, { value }) => setDraft((d) => ({ ...d, from: value }))}
          style={{ minWidth: 170 }}
        />
        <Icon name="arrow right" style={{ color: '#aaa' }} />
        <Input
          size="small" placeholder="Say it like…" value={draft.to} disabled={busy}
          onChange={(_, { value }) => setDraft((d) => ({ ...d, to: value }))}
          style={{ minWidth: 170 }}
        />
        <Dropdown
          selection compact options={MODE_OPTIONS} value={draft.mode} disabled={busy}
          onChange={(_, { value }) => setDraft((d) => ({ ...d, mode: value }))}
        />
        <Checkbox
          label="Match case" checked={draft.caseSensitive} disabled={busy}
          onChange={(_, { checked }) => setDraft((d) => ({ ...d, caseSensitive: checked }))}
        />
        {/* ⭐ ONE BUTTON, TWO ACTS. With a word alone it FINDS AND HEARS — three takes of the book
            spelling, which answers "is this wrong at all". With a respelling too it AUDITIONS,
            control against candidate. Gating this on both fields meant you could not hear a word
            without first inventing a fix for it. */}
        <Button size="small" basic color="teal" disabled={!canHear || auditioning}
          loading={auditioning} onClick={audition}>
          <Icon name={dirty ? 'exchange' : 'search'} /> {dirty ? 'Audition' : 'Find & hear'}
        </Button>
        <Button size="small" color="teal" disabled={!dirty || busy}
          onClick={async () => { setImpact(await measure(draft)); setConfirmSave(true); }}>
          <Icon name={isEdit ? 'save' : 'plus'} /> {isEdit ? 'Update' : 'Add'}
        </Button>
        {/* The other outcome of hearing a word: nothing is wrong with it. */}
        <Button size="small" basic disabled={!canHear || busy} onClick={markFine}>
          <Icon name="check" /> Fine as-is
        </Button>
        {isEdit && <Button size="small" basic disabled={busy} onClick={reset}>Cancel</Button>}
      </div>

      <div style={{ fontSize: '0.78em', color: '#999', marginTop: '0.4em' }}>
        <strong>Exact word</strong> matches only that word. <strong>Word + endings</strong> also
        catches plurals and derivatives — one entry for <code>wyvern</code> covers
        <code> wyverns</code>, <code>wyvernic</code>, <code>wyvernized</code>.
        {' '}<strong>Match case</strong> when only one casing is wrong — <code>WAXWANDS</code> misreads
        in display caps while <code>WaxWands</code> in the prose is already right.
      </div>

      {/* ⭐ SAY SO BEFORE THEY SPEND ANYTHING. Typing a word already judged fine should not lead to
          a paid audition and a puzzled "didn't I do this?" — the register exists precisely so the
          same decision is not made twice. */}
      {clearedHit && !isEdit && (
        <Message size="tiny" style={{ marginTop: '0.6em' }}>
          <Icon name="check circle outline" /> You already listened to <code>{clearedHit.word}</code>
          {' '}and marked it fine as-is
          {clearedHit.clearedAt ? ` on ${String(clearedHit.clearedAt).slice(0, 10)}` : ''}.{' '}
          <a href="#undecide" onClick={(ev) => { ev.preventDefault(); unclear(clearedHit.word); }}>
            Put it back on the list
          </a>{' '}— that costs nothing.
        </Message>
      )}

      {error && <Message negative size="tiny" onDismiss={() => setError(null)} style={{ marginTop: '0.6em' }}>{error}</Message>}
      {msg && <Message info size="tiny" onDismiss={() => setMsg(null)} style={{ marginTop: '0.6em' }}>{msg}</Message>}

      {/* ── which passage the audition uses ────────────────────────────── */}
      <Segment
        secondary
        style={{
          marginTop: '0.75em', padding: '0.6em 0.8em',
          border: dropActive ? '2px dashed #21ba45' : undefined,
        }}
        onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropActive(false);
          const t = e.dataTransfer?.getData('text/plain');
          if (t && t.trim()) setPasted(t);
        }}
      >
        <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '0.4em' }}>
          <Icon name="crosshairs" /> <strong>Audition passage</strong>
          {passage ? (
            <Button
              size="mini" basic compact floated="right"
              onClick={() => { setPasted(''); if (onClearCapture) onClearCapture(); setTakes(null); }}
            >
              Clear
            </Button>
          ) : null}
        </div>

        {passage ? (
          <>
            <div style={{ fontSize: '0.78em', color: '#888', marginBottom: '0.3em' }}>
              {passage.kind === 'captured'
                ? <>Captured from line <code>{captured.lineId}</code>{captured.speaker ? <> · {captured.speaker}</> : null}</>
                : <>Pasted text</>}
              {' '}— {passage.text.length.toLocaleString()} characters, used as-is.
            </div>
            <div style={{ fontSize: '0.82em', fontStyle: 'italic', color: '#555' }}>
              “{passage.text.slice(0, 260)}{passage.text.length > 260 ? '…' : ''}”
            </div>
          </>
        ) : (
          <div style={{ fontSize: '0.78em', color: '#888' }}>
            {/* ⚠️ Naming the default out loud, because it is the part that misled once already. */}
            None chosen — the audition will search {bookTitle || 'this book'} and use the
            <strong> longest</strong> passage containing the word. That may not be the one you heard
            fail. Press <Icon name="crosshairs" fitted /> beside a line in the list above to audition
            that line instead, or drop text here.
          </div>
        )}

        <textarea
          rows={2}
          value={pasted}
          placeholder="…or drop / paste a passage here"
          onChange={(e) => setPasted(e.target.value)}
          style={{
            width: '100%', marginTop: '0.5em', fontSize: '0.8em', fontFamily: 'inherit',
            padding: '0.4em', border: '1px solid #ddd', borderRadius: 3, resize: 'vertical',
          }}
        />
      </Segment>

      {/* ── the audition ───────────────────────────────────────────────── */}
      {takes?.found && (
        <Segment secondary style={{ marginTop: '0.75em' }}>
          {/* ⚠️ SAY WHICH PASSAGE THESE TAKES ARE. Two auditions of the same word in different
              passages are not comparable, and the difference is invisible once the audio is
              playing — which is how a book-search result got read as a verdict on a captured line. */}
          <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '0.5em' }}>
            {takes.source === 'capture' ? (
              <>
                From the <strong>passage you supplied</strong>
                {takes.trimmed
                  ? <> — trimmed to {String(takes.excerpt).length.toLocaleString()} characters from the front, keeping the run-up nearest the word.</>
                  : <>, used verbatim ({String(takes.excerpt).length.toLocaleString()} characters).</>}
              </>
            ) : (
              <>
                From <strong>{bookTitle || 'this book'}</strong> — {takes.occurrences} passage
                {takes.occurrences === 1 ? '' : 's'} contain this word, and the longest was used.
                Rendered in its own passage, because a word can read correctly alone and still fail
                mid-paragraph.
              </>
            )}
          </div>
          <div style={{ fontSize: '0.82em', fontStyle: 'italic', color: '#555', marginBottom: '0.75em' }}>
            “…{String(takes.excerpt).slice(-220)}”
          </div>
          {(takes.findOnly
            ? [['How it reads now', grouped.control]]
            : [['Book spelling', grouped.control], ['Respelled', grouped.candidate]]
          ).map(([label, list]) => (
            <div key={label} style={{ display: 'flex', gap: '0.5em', alignItems: 'center', marginBottom: '0.4em' }}>
              <Label size="tiny" style={{ minWidth: 110 }}>{label}</Label>
              {/* ⚠️ NUMBERED, because "only the first one failed" has to be a sayable sentence.
                  The takes were rendered in order and laid out in order, and the operator still
                  had to ask which was which — a report you cannot address to a specific take is a
                  report that cannot be checked. ⓘ The numbers are LABELS, not a sequence: each
                  take is an independent generation, so take 1 is not special and the order
                  carries no meaning of its own. */}
              {list.map((t) => (
                <div key={`${t.kind}-${t.take}`} style={{ display: 'flex', alignItems: 'center', gap: '0.25em' }}>
                  <span style={{ fontSize: '0.72em', color: '#aaa', fontVariantNumeric: 'tabular-nums' }}>
                    {t.take}
                  </span>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio controls preload="none" style={{ height: 32 }}
                    src={`data:${t.contentType};base64,${t.audio}`} />
                </div>
              ))}
            </div>
          ))}
          {/* ⚠️ Three takes because the fault is INTERMITTENT — and a reminder of what this cannot show. */}
          <div style={{ fontSize: '0.76em', color: '#999', marginTop: '0.5em' }}>
            Three takes{takes.findOnly ? '' : ' a side'}: a word that fails one time in five sounds
            fine in a single preview. Emphasis also varies between takes — that is the model reading,
            not the spelling, and no entry changes it.
            {takes.findOnly && (
              <>
                {' '}⚠️ Sounding right in three takes does not clear a word — it can still fail in a
                passage this one does not resemble.
              </>
            )}
          </div>
          {takes.findOnly && (
            <div style={{ fontSize: '0.8em', marginTop: '0.6em', color: '#666' }}>
              Wrong? Type a respelling above and press <strong>Audition</strong> to compare.
              Right? Press <strong>Fine as-is</strong> and it will not be raised again.
            </div>
          )}
        </Segment>
      )}

      {/* ── the list ───────────────────────────────────────────────────── */}
      {entries.length > 0 ? (
        <List divided relaxed size="small" style={{ marginTop: '0.9em' }}>
          {entries.map((e) => (
            <List.Item key={e.from} style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <List.Content style={{ flex: 1, cursor: 'pointer' }}
                onClick={() => { setDraft({ ...BLANK, ...e }); setEditingFrom(e.from); setTakes(null); }}>
                <code>{e.from}</code> <Icon name="arrow right" size="small" style={{ color: '#bbb' }} /> <code>{e.to}</code>
                {e.mode === 'stem' && <Label size="mini" style={{ marginLeft: 6 }}>+ endings</Label>}
                {e.caseSensitive && <Label size="mini" style={{ marginLeft: 4 }}>case</Label>}
              </List.Content>
              <Button size="mini" basic icon disabled={busy}
                onClick={async () => { setImpact(await measure(e)); setConfirmDelete(e.from); }}>
                <Icon name="trash alternate outline" />
              </Button>
            </List.Item>
          ))}
        </List>
      ) : (
        <div style={{ fontSize: '0.82em', color: '#999', marginTop: '0.9em' }}>
          No respellings yet. Add one when you hear a name read wrongly.
        </div>
      )}

      {/* ── heard, and deliberately left alone ─────────────────────────── */}
      {cleared.length > 0 && (
        <div style={{ marginTop: '0.9em' }}>
          <div style={{ fontSize: '0.78em', color: '#888', marginBottom: '0.3em' }}>
            {/* ⚠️ A DECISION, NOT AN ABSENCE. These were listened to and judged correct; that is why
                they are not raised again. Undoing it is free — no audio anywhere depends on it. */}
            <Icon name="check circle outline" /> Heard and left alone — {cleared.length} word
            {cleared.length === 1 ? '' : 's'}. Undoing this changes no audio.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3em' }}>
            {cleared.map((c) => (
              <Label key={c.word} size="tiny" style={{ cursor: 'default' }}>
                {c.word}
                <Icon name="delete" onClick={() => (busy ? null : unclear(c.word))}
                  title="Put this word back on the list to decide" />
              </Label>
            ))}
          </div>
        </div>
      )}

      <Confirm
        open={confirmSave}
        header={isEdit ? 'Update this respelling?' : 'Add this respelling?'}
        content={
          <div className="content">
            <p>
              <code>{draft.from}</code> will be spoken as <code>{draft.to}</code> in{' '}
              <strong>every book in this Literary Group</strong>. The books themselves are not
              changed — only what is sent to the voice.
            </p>
            <ImpactNotice impact={impact} bookTitle={bookTitle} />
          </div>
        }
        confirmButton={isEdit ? 'Update' : 'Add'}
        onCancel={() => { setConfirmSave(false); setImpact(null); }} onConfirm={commit}
      />
      <Confirm
        open={!!confirmDelete}
        header="Remove this respelling?"
        content={
          <div className="content">
            <p>
              <code>{confirmDelete}</code> will go back to being spoken as written, in every book in
              this group.
            </p>
            {/* ⚠️ REMOVAL IS NOT THE SAFE DIRECTION. Taking an entry out changes what the narrator
                says just as adding it did, so it invalidates exactly the same rendered lines. The
                only difference is which way the sound moves. */}
            <ImpactNotice impact={impact} bookTitle={bookTitle} verb="Removing this" />
          </div>
        }
        confirmButton="Remove"
        onCancel={() => { setConfirmDelete(null); setImpact(null); }}
        onConfirm={() => remove(confirmDelete)}
      />
    </Segment>
  );
}

/**
 * What this decision costs, in the only terms that matter to someone who has already rendered.
 *
 * ⭐ THE SAME DECISION IS FREE OR EXPENSIVE DEPENDING ONLY ON WHEN IT IS MADE. Before a render:
 * nothing. Measured on LN1 afterwards, one name invalidated 330 of 1,483 lines — 22% of the book's
 * audio, ~292,579 characters, ~47 minutes of TTS, then a re-stitch of every chapter touched. Saying
 * so at the moment of decision is the only place it can change anyone's mind.
 *
 * ⚠️ A published book is the case this exists for. Corrected audio reaches listeners only through a
 * republish, and that cost is the operator's distribution reality — not something we can size.
 */
function ImpactNotice({ impact, bookTitle, verb = 'This' }) {
  if (!impact) {
    return (
      <p style={{ color: '#999', fontSize: '0.9em' }}>
        Already-rendered lines keep their existing audio until you force a re-render.
      </p>
    );
  }
  if (impact.free) {
    return (
      <p style={{ color: '#21ba45' }}>
        ⭐ <strong>Free.</strong> Nothing carrying this word has been rendered yet
        {impact.occurrences ? ` (${impact.occurrences.toLocaleString()} occurrences waiting)` : ''}.
        Deciding now costs nothing.
      </p>
    );
  }
  const pct = impact.renderedTotal
    ? Math.round((impact.staleLines / impact.renderedTotal) * 100) : 0;
  return (
    <>
      <p style={{ color: '#db2828' }}>
        ⚠️ {verb} leaves <strong>{impact.staleLines.toLocaleString()} already-rendered lines</strong>
        {' '}in <strong>{bookTitle || 'this book'}</strong> no longer matching what the pipeline would
        say{pct ? ` — ${pct}% of that book's audio` : ''}.
      </p>
      <p style={{ fontSize: '0.9em', color: '#666' }}>
        Re-rendering bills ~{impact.staleChars.toLocaleString()} characters
        {impact.estimatedMinutes ? ` and takes ~${impact.estimatedMinutes} minutes` : ''}, then every
        affected chapter must be re-stitched. <strong>If this book is published, corrected audio
        reaches listeners only if you republish it.</strong>
      </p>
      <p style={{ fontSize: '0.9em', color: '#666' }}>
        Nothing is re-rendered automatically. The entry applies to future renders either way — so you
        can take it now for the group&apos;s other books and leave this book&apos;s audio alone.
      </p>
    </>
  );
}
